import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBreadcrumb from "@/components/base/PageBreadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";

const PAGE_SIZE = 4;

export default function NewsCategoryPage() {
  usePageMeta(
    "影视资讯 - 日韩欧美影视平台",
    "影视资讯频道汇集日韩与欧美影视行业动态、票房消息、剧集预告与深度报道，帮助你第一时间掌握最新影视资讯。",
    "影视资讯,影视新闻,行业动态,票房,剧集资讯",
  );

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(newsList.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = newsList.slice(start, start + PAGE_SIZE);

  const goPage = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6">
        <PageBreadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />
        <div className="mt-4 mb-6">
          <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-950 font-heading">
            影视资讯
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            行业动态、票房战绩、剧集预告与深度报道，汇聚日韩与欧美影视圈的最新消息，让你不错过每一条值得关注的资讯。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {pageItems.map((n) => (
              <article
                key={n.id}
                className="bg-background-50 border border-background-200 rounded-lg overflow-hidden card-hover"
              >
                <Link to={`/news/${n.id}`} className="flex flex-col sm:flex-row group">
                  <div className="w-full sm:w-56 h-44 sm:h-auto shrink-0 overflow-hidden bg-background-200">
                    <img
                      src={n.cover}
                      alt={n.title}
                      title={`${n.title} 影视资讯`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex-1 p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-accent-100 text-accent-900">
                        {n.category}
                      </span>
                      <span className="text-xs text-foreground-500">{n.date}</span>
                    </div>
                    <h2 className="text-base md:text-lg font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
                      {n.title}
                    </h2>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {n.summary}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary-600">
                      阅读全文
                      <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>

          <aside className="space-y-5">
            <div className="bg-background-50 border border-background-200 rounded-lg p-5">
              <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950 font-heading mb-3">
                <i className="ri-fire-fill text-accent-500" />
                热门推荐
              </h3>
              <ul className="space-y-3">
                {newsList.slice(0, 5).map((n, i) => (
                  <li key={n.id}>
                    <Link
                      to={`/news/${n.id}`}
                      className="flex gap-3 group"
                    >
                      <span className="text-sm font-extrabold font-heading text-background-300 w-5 shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-sm text-foreground-700 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {n.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-secondary-50 border border-secondary-100 rounded-lg p-5">
              <h3 className="text-base font-bold text-foreground-950 font-heading mb-2">
                订阅影视资讯
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                想第一时间获取票务与上线消息？可以前往「关于我们」查看内容说明与联系方式。
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-secondary-700 hover:text-secondary-900 transition-colors"
              >
                了解详情
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              type="button"
              onClick={() => goPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className={`w-9 h-9 flex items-center justify-center rounded-md border text-sm transition-colors ${
                page === 1
                  ? "border-background-200 text-foreground-300 cursor-not-allowed"
                  : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 cursor-pointer"
              }`}
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => goPage(p)}
                className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  p === page
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => goPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className={`w-9 h-9 flex items-center justify-center rounded-md border text-sm transition-colors ${
                page === totalPages
                  ? "border-background-200 text-foreground-300 cursor-not-allowed"
                  : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600 cursor-pointer"
              }`}
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}